import { ScheduleServiceDialog } from "@/components/site/ScheduleServiceDialog";
import { PhoneInput } from "@/components/site/PhoneInput";
import { useRef, useState, type FormEvent } from "react";
import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/site/PageLayout";
import { Seo, breadcrumbJsonLd, organizationJsonLd } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { useCopy, useLocale } from "@/i18n/LocaleContext";
import {
  SITE_ADDRESS_DISPLAY,
  SITE_EMAIL,
  SITE_MAP_EMBED_QUERY,
  SITE_MAP_URL,
  SITE_PHONE_DISPLAY,
} from "@/routes-data";

/** Stable submitted values; labels come from COPY. */
const TOPIC_VALUES = ["General question", "Schedule service / repair", "Parts inquiry", "Wholesale / fleet pricing", "Something else"];

const COPY = {
  en: {
    seo: { title: "Contact Our Elkhart, IN Trailer Parts & Service Shop", description: "Reach Axle Inc. in Elkhart, IN: call (574) 264-9434, schedule trailer service online, or stop by 53664 Co Rd 9. Serving Elkhart County and SW Michigan." },
    nav: { home: "Home", contact: "Contact" },
    header: { tag: "Contact", title: "Talk to a real person.", subtitle: "Call the shop, send a message, or schedule service online. We answer fast - usually the same day." },
    sidebar: {
      visit: "Visit the shop",
      address: "53664 Co Rd 9\nElkhart, IN 46514\nUnited States",
      call: "Call us",
      email: "Email us",
      location: "Visit us",
      schedule: "Schedule service online",
      parts: "Order parts online",
      hours: { label: "Hours", monFri: "Mon–Fri: 8:00am – 5:00pm", closed: "Closed Sat & Sun" },
      mapTitle: "Axle Inc. - 53664 Co Rd 9, Elkhart, IN 46514"
    },
    form: {
      title: "Send us a message",
      subtitle: "We typically respond within one business day.",
      success: { title: "Thanks - we got it.", body: (phone: string) => `A team member will reach out shortly. For urgent service, call ${phone}.` },
      name: "Name",
      phone: "Phone",
      email: "Email",
      topic: { label: "What can we help with?", options: ["General question", "Schedule service / repair", "Parts inquiry", "Wholesale / fleet pricing", "Something else"] },
      message: "Message",
      submit: "Send message",
      disclaimer: "We'll never share your information."
    }
  },
  es: {
    seo: { title: "Contacte nuestra tienda de partes y servicio de remolques en Elkhart, IN", description: "Comuníquese con Axle Inc. en Elkhart, IN: llame al (574) 264-9434, programe el servicio de remolque en línea o visítenos en 53664 Co Rd 9. Servimos al condado de Elkhart y el suroeste de Michigan." },
    nav: { home: "Inicio", contact: "Contacto" },
    header: { tag: "Contacto", title: "Hable con una persona real.", subtitle: "Llame a la tienda, envíe un mensaje o programe el servicio en línea. Respondemos rápido, generalmente el mismo día." },
    sidebar: {
      visit: "Visite la tienda",
      address: "53664 Co Rd 9\nElkhart, IN 46514\nEstados Unidos",
      call: "Llámenos",
      email: "Envíenos un correo",
      location: "Visítenos",
      schedule: "Programar servicio en línea",
      parts: "Pedir partes en línea",
      hours: { label: "Horario", monFri: "Lun–Vie: 8:00am – 5:00pm", closed: "Cerrado Sáb y Dom" },
      mapTitle: "Axle Inc. - 53664 Co Rd 9, Elkhart, IN 46514"
    },
    form: {
      title: "Envíenos un mensaje",
      subtitle: "Generalmente respondemos dentro de un día hábil.",
      success: { title: "Gracias, lo hemos recibido.", body: (phone: string) => `Un miembro del equipo se pondrá en contacto pronto. Para servicio urgente, llame al ${phone}.` },
      name: "Nombre",
      phone: "Teléfono",
      email: "Correo electrónico",
      topic: { label: "¿En qué podemos ayudarle?", options: ["Pregunta general", "Programar servicio / reparación", "Consulta de partes", "Precios al por mayor / flota", "Otro motivo"] },
      message: "Mensaje",
      submit: "Enviar mensaje",
      disclaimer: "Nunca compartiremos su información."
    }
  }
};

export default function Contact() {
  const t = useCopy(COPY);
  const { locale: language } = useLocale();
  const [submitted, setSubmitted] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const submissionKey = useRef(crypto.randomUUID());
  const previousPayload = useRef("");
  const submitting = useRef(false);
  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setPending(true);
    setError("");
    const data = Object.fromEntries(new FormData(event.currentTarget));
    const payload = JSON.stringify(data);
    if (previousPayload.current && previousPayload.current !== payload) {
      submissionKey.current = crypto.randomUUID();
    }
    previousPayload.current = payload;
    try {
      const response = await fetch("/api/contact-leads", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, submissionKey: submissionKey.current }),
      });
      if (!response.ok) throw new Error("submission failed");
      setSubmitted(true);
    } catch {
      setError(language === "es" ? "No pudimos enviar su mensaje. Inténtelo de nuevo o llámenos." : "We couldn't submit your message. Please try again or call us.");
    } finally {
      submitting.current = false;
      setPending(false);
    }
  }
  return (
    <PageLayout>
      <Seo
        title={t.seo.title}
        description={t.seo.description}
        path="/contact/"
        jsonLd={[organizationJsonLd(), breadcrumbJsonLd([
          { name: t.nav.home, path: "/" },
          { name: t.nav.contact, path: "/contact/" },
        ])]}
      />
      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="mb-6 [&_a]:text-secondary-foreground/70 [&_a:hover]:text-white [&_span]:text-white">
            <Breadcrumbs trail={[{ name: t.nav.home, path: "/" }, { name: t.nav.contact, path: "/contact/" }]} />
          </div>
          <p className="text-xs md:text-sm uppercase tracking-[0.22em] text-white font-semibold mb-4">{t.header.tag}</p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight">{t.header.title}</h1>
          <p className="mt-5 text-lg text-secondary-foreground/80 max-w-2xl leading-relaxed">
            {t.header.subtitle}
          </p>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2 space-y-8">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">{t.sidebar.visit}</h2>
            <p className="text-lg font-medium mb-1">Axle Inc.</p>
            <a href={SITE_MAP_URL} target="_blank" rel="noopener" className="text-foreground/80 leading-relaxed hover:text-primary transition-colors block">
              {t.sidebar.address}
            </a>
          </div>
          <div className="space-y-4">
            <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`} className="flex items-start gap-3 group">
              <Phone className="h-5 w-5 mt-0.5 text-primary shrink-0" />
              <div>
                <div className="text-sm text-muted-foreground">{t.sidebar.call}</div>
                <div className="font-medium group-hover:text-primary transition-colors">{SITE_PHONE_DISPLAY}</div>
              </div>
            </a>
            <a href={`mailto:${SITE_EMAIL}`} className="flex items-start gap-3 group">
              <Mail className="h-5 w-5 mt-0.5 text-primary shrink-0" />
              <div>
                <div className="text-sm text-muted-foreground">{t.sidebar.email}</div>
                <div className="font-medium group-hover:text-primary transition-colors">{SITE_EMAIL}</div>
              </div>
            </a>
            <a href={SITE_MAP_URL} target="_blank" rel="noopener" className="flex items-start gap-3 group">
              <MapPin className="h-5 w-5 mt-0.5 text-primary shrink-0" />
              <div>
                <div className="text-sm text-muted-foreground">{t.sidebar.location}</div>
                <div className="font-medium group-hover:text-primary transition-colors">{SITE_ADDRESS_DISPLAY} →</div>
              </div>
            </a>
            <ScheduleServiceDialog>
              <button type="button" className="flex items-start gap-3 group text-left w-full">
                <Clock className="h-5 w-5 mt-0.5 text-primary shrink-0" />
                <div>
                  <div className="text-sm text-muted-foreground">{t.sidebar.schedule}</div>
                  <div className="font-medium group-hover:text-primary transition-colors">axle.setmore.com →</div>
                </div>
              </button>
            </ScheduleServiceDialog>
            <a href="https://parts.axleinc.com/" target="_blank" rel="noopener" className="flex items-start gap-3 group">
              <Mail className="h-5 w-5 mt-0.5 text-primary shrink-0" />
              <div>
                <div className="text-sm text-muted-foreground">{t.sidebar.parts} (en inglés)</div>
                <div className="font-medium group-hover:text-primary transition-colors">parts.axleinc.com →</div>
              </div>
            </a>
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 mt-0.5 text-primary shrink-0" />
              <div>
                <div className="text-sm text-muted-foreground">{t.sidebar.hours.label}</div>
                <div className="font-medium">{t.sidebar.hours.monFri}</div>
                <div className="text-sm text-muted-foreground">{t.sidebar.hours.closed}</div>
              </div>
            </div>
          </div>
          <div className="rounded-md overflow-hidden border border-border h-72">
            <iframe title={t.sidebar.mapTitle} src={`https://www.google.com/maps?q=${SITE_MAP_EMBED_QUERY}&output=embed`} width="100%" height="100%" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="border border-border rounded-md p-6 md:p-8 bg-card">
            <h2 className="text-xl md:text-2xl font-semibold mb-1">{t.form.title}</h2>
            <p className="text-sm text-muted-foreground mb-6">{t.form.subtitle}</p>
            {submitted ? (
              <div className="rounded-md bg-primary/5 border border-primary/20 p-6 text-center">
                <p className="font-semibold text-foreground mb-1">{t.form.success.title}</p>
                <p className="text-sm text-muted-foreground">{t.form.success.body(SITE_PHONE_DISPLAY)}</p>
              </div>
            ) : (
              <form className="space-y-4" onSubmit={onSubmit}>
                <input name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label={t.form.name} name="name" required />
                  <Field label={t.form.phone} name="phone" type="tel" />
                </div>
                <Field label={t.form.email} name="email" type="email" required />
                <div>
                  <label htmlFor="preferredLanguage" className="block text-sm font-medium mb-1.5">{language === "es" ? "Idioma preferido" : "Preferred language"}</label>
                  <input id="preferredLanguage" name="preferredLanguage" defaultValue="English" maxLength={80} required className="w-full px-3 py-2.5 border border-input rounded-md bg-background text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" htmlFor="topic">{t.form.topic.label}</label>
                  <select id="topic" name="topic" className="w-full px-3 py-2.5 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                    {t.form.topic.options.map((o, i) => <option key={TOPIC_VALUES[i]} value={TOPIC_VALUES[i]}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" htmlFor="message">{t.form.message}</label>
                  <textarea id="message" name="message" required rows={5} className="w-full px-3 py-2.5 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                </div>
                {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
                <Button type="submit" disabled={pending} size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  {t.form.submit}
                </Button>
                <p className="text-xs text-muted-foreground">{t.form.disclaimer}</p>
              </form>
            )}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}

function Field({ label, name, type = "text", required }: { label: string; name: string; type?: string; required?: boolean }) {
  return (
    <div>
      <label htmlFor={name} className="block text-sm font-medium mb-1.5">
        {label} {required && <span className="text-primary-strong">*</span>}
      </label>
      {type === "tel" ? (
        <PhoneInput id={name} name={name} required={required} className="w-full px-3 py-2.5 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
      ) : (
        <input id={name} name={name} type={type} required={required} className="w-full px-3 py-2.5 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
      )}
    </div>
  );
}
